import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { WorldCategoryDto } from '@/api/types'
import {
  useCreateWorldCategory,
  useDeleteWorldCategory,
  useUpdateWorldCategory,
  worldCategoriesAdminQuery,
} from '@/features/platform/platform.queries'
import { formatNumber } from '@/lib/format'
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  Dialog,
  PageHeader,
  TextField,
  useToast,
  type Column,
} from '@/ui'
import { IconAdd, IconDelete, IconHidden, IconVisible } from '@/icons'

export const Route = createFileRoute('/_authed/categories/')({
  component: CategoriesPage,
})

function CategoriesPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()

  const query = useQuery(worldCategoriesAdminQuery)
  const create = useCreateWorldCategory()
  const update = useUpdateWorldCategory()
  const remove = useDeleteWorldCategory()

  const [isAdding, setIsAdding] = useState(false)
  const [deleting, setDeleting] = useState<WorldCategoryDto | null>(null)
  const [key, setKey] = useState('')
  const [name, setName] = useState('')

  const columns: Column<WorldCategoryDto>[] = [
    {
      id: 'name',
      header: t('categories.name'),
      cell: (category) => <span className="font-medium text-text">{category.name}</span>,
    },
    {
      id: 'key',
      header: t('categories.key'),
      cell: (category) => (
        <span className="font-mono text-xs text-text-secondary">{category.key}</span>
      ),
    },
    {
      id: 'worlds',
      header: t('categories.worlds'),
      align: 'end',
      cell: (category) => (
        <span data-numeric>{formatNumber(category.worldCount, i18n.language)}</span>
      ),
    },
    {
      id: 'active',
      header: t('categories.active'),
      cell: (category) => (
        <Badge tone={category.isActive ? 'success' : 'neutral'} hasDot>
          {category.isActive ? t('common.yes') : t('common.no')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      width: '3rem',
      cell: (category) => (
        <ActionMenu
          actions={[
            {
              id: 'visibility',
              label: category.isActive ? t('common.no') : t('common.yes'),
              icon: category.isActive ? <IconHidden size={15} /> : <IconVisible size={15} />,
              onSelect: () => {
                update.mutate(
                  { key: category.key, isActive: !category.isActive },
                  {
                    onSuccess: () => {
                      toast.success(t('categories.updated'))
                    },
                    onError: (error) => {
                      toast.fromError(error, t('state.errorTitle'))
                    },
                  },
                )
              },
            },
            {
              id: 'delete',
              label: t('common.delete'),
              icon: <IconDelete size={15} />,
              isDestructive: true,
              onSelect: () => {
                setDeleting(category)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('categories.title')}
        description={t('categories.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<IconAdd size={15} />}
            onClick={() => {
              setIsAdding(true)
            }}
          >
            {t('categories.add')}
          </Button>
        }
      />

      <Card isFlush>
        <DataTable
          columns={columns}
          rows={query.data?.items ?? []}
          getRowId={(category) => category.key}
          caption={t('categories.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          emptyTitle={t('categories.empty')}
        />
      </Card>

      <Dialog
        isOpen={isAdding}
        onClose={() => {
          setIsAdding(false)
        }}
        title={t('categories.add')}
        isDismissable={!create.isPending}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setIsAdding(false)
              }}
              disabled={create.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="add-category"
              isLoading={create.isPending}
            >
              {t('common.create')}
            </Button>
          </>
        }
      >
        <form
          id="add-category"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            create.mutate(
              { key, name },
              {
                onSuccess: () => {
                  toast.success(t('categories.created'))
                  setKey('')
                  setName('')
                  setIsAdding(false)
                },
                onError: (error) => {
                  toast.fromError(error, t('state.errorTitle'))
                },
              },
            )
          }}
        >
          <TextField
            label={t('categories.name')}
            required
            value={name}
            onChange={(event) => {
              setName(event.target.value)
            }}
          />
          <TextField
            label={t('categories.key')}
            hint={t('categories.keyHint')}
            required
            pattern="[a-z0-9-]+"
            value={key}
            onChange={(event) => {
              setKey(event.target.value.toLowerCase().replace(/\s+/g, '-'))
            }}
          />
        </form>
      </Dialog>

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null)
        }}
        onConfirm={() => {
          if (!deleting) return
          remove.mutate(deleting.key, {
            onSuccess: () => {
              toast.success(t('categories.deleted'))
              setDeleting(null)
            },
            onError: (error) => {
              toast.fromError(error, t('state.errorTitle'))
            },
          })
        }}
        title={t('categories.deleteConfirm')}
        body={
          deleting && deleting.worldCount > 0
            ? t('categories.notEmpty', { name: deleting.name, count: deleting.worldCount })
            : t('categories.deleteBody', { name: deleting?.name ?? '' })
        }
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />
    </>
  )
}
